// ============================================
// VIDEO ANALYZER + Video Protocol Drafts
// ============================================

import { supabase } from '../../lib/supabase';
import { uuid } from '../../utils/uuid';
import type { AnalyzedVideo, UserSavedVideo, AnalyzeVideoResponse, ExtractedProtocol } from '../videoTypes';
import { requireUserId, throwWithContext, notify } from './_shared';

/* eslint-disable @typescript-eslint/no-explicit-any */

function mapAnalyzedVideoFromDb(row: any): AnalyzedVideo {
  return {
    id: row.id,
    videoUrl: row.video_url,
    videoId: row.video_id,
    title: row.title,
    creatorName: row.creator_name,
    thumbnailUrl: row.thumbnail_url,
    duration: row.duration,
    extractedData: row.extracted_data,
    factCheck: row.fact_check,
    peptideIds: row.peptide_ids || [],
    isPeptideRelated: row.is_peptide_related,
    processedAt: row.processed_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export async function analyzeVideo(videoUrl: string): Promise<AnalyzeVideoResponse> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

  const res = await fetch(`${supabaseUrl}/functions/v1/analyze-video`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'apikey': supabaseAnonKey,
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ video_url: videoUrl }),
  });

  const body = await res.json();

  if (!res.ok) {
    console.error('analyze-video error:', res.status, body);
    throw new Error(body?.message || `Edge function returned ${res.status}`);
  }

  return body as AnalyzeVideoResponse;
}

export async function fetchSavedVideos(): Promise<UserSavedVideo[]> {
  const userId = await requireUserId();
  const { data, error } = await supabase
    .from('user_saved_videos')
    .select(`
      id,
      user_id,
      video_id,
      saved_at,
      notes,
      analyzed_videos (
        id, video_url, video_id, title, creator_name, thumbnail_url,
        duration, extracted_data, fact_check, peptide_ids,
        is_peptide_related, processed_at, created_at, updated_at
      )
    `)
    .eq('user_id', userId)
    .order('saved_at', { ascending: false });

  if (error) throwWithContext("Couldn't load your saved videos", error);

  return (data || []).map((row: any) => ({
    id: row.id,
    userId: row.user_id,
    videoId: row.video_id,
    savedAt: row.saved_at,
    notes: row.notes,
    video: row.analyzed_videos ? mapAnalyzedVideoFromDb(row.analyzed_videos) : undefined,
  }));
}

export async function saveVideo(analyzedVideoId: string): Promise<void> {
  const userId = await requireUserId();
  const { error } = await supabase
    .from('user_saved_videos')
    .insert({ user_id: userId, video_id: analyzedVideoId });
  if (error) {
    if (error.code === '23505') return;
    throwWithContext("Couldn't save this video", error);
  }
  notify('user_saved_videos');
}

export async function unsaveVideo(savedVideoId: string): Promise<void> {
  const userId = await requireUserId();
  const { error } = await supabase
    .from('user_saved_videos')
    .delete()
    .eq('id', savedVideoId)
    .eq('user_id', userId);
  if (error) throwWithContext("Couldn't remove this saved video", error);
  notify('user_saved_videos');
}

export async function fetchVideoUsageThisWeek(): Promise<number> {
  const userId = await requireUserId();
  const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
  const { count, error } = await supabase
    .from('video_analysis_usage')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .gte('analyzed_at', weekAgo);
  if (error) return 0;
  return count || 0;
}

// ============================================
// Video Protocol Drafts (Step 6)
// ============================================

export interface VideoProtocolDraft {
  id: string;
  userId: string;
  videoId?: string;
  videoTitle?: string;
  creatorName?: string;
  extractedData: ExtractedProtocol;
  isUserSaved: boolean;
  savedAt?: string;
  expiresAt: string;
  createdAt: string;
}

export async function saveVideoProtocolDraft(
  draft: Omit<VideoProtocolDraft, 'id' | 'userId' | 'createdAt' | 'expiresAt'> & { expiresAt?: string },
): Promise<VideoProtocolDraft> {
  const userId = await requireUserId();
  const id = uuid();
  const now = new Date();
  const expiresAt = draft.expiresAt ?? new Date(now.getTime() + 60 * 60 * 1000).toISOString();

  const row = {
    id,
    user_id: userId,
    video_id: draft.videoId ?? null,
    extracted_data: draft.extractedData,
    is_user_saved: draft.isUserSaved ?? false,
    saved_at: draft.isUserSaved ? now.toISOString() : null,
    expires_at: expiresAt,
    created_at: now.toISOString(),
  };

  const { error } = await supabase.from('video_protocol_drafts').insert(row);
  if (error) throwWithContext("Couldn't save draft", error);

  return {
    id,
    userId,
    videoId: draft.videoId,
    extractedData: draft.extractedData,
    isUserSaved: draft.isUserSaved ?? false,
    savedAt: draft.isUserSaved ? now.toISOString() : undefined,
    expiresAt,
    createdAt: now.toISOString(),
  };
}

export async function fetchVideoProtocolDraft(id: string): Promise<VideoProtocolDraft | null> {
  const userId = await requireUserId();
  const { data, error } = await supabase
    .from('video_protocol_drafts')
    .select('*')
    .eq('id', id)
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throwWithContext("Couldn't load draft", error);
  if (!data) return null;
  return {
    id: data.id,
    userId: data.user_id,
    videoId: data.video_id ?? undefined,
    extractedData: data.extracted_data,
    isUserSaved: data.is_user_saved,
    savedAt: data.saved_at ?? undefined,
    expiresAt: data.expires_at,
    createdAt: data.created_at,
  };
}

export async function fetchSavedDrafts(): Promise<VideoProtocolDraft[]> {
  const userId = await requireUserId();
  const { data, error } = await supabase
    .from('video_protocol_drafts')
    .select('*, analyzed_videos(title, creator_name)')
    .eq('user_id', userId)
    .eq('is_user_saved', true)
    .order('saved_at', { ascending: false });
  if (error) throwWithContext("Couldn't load saved drafts", error);
  return (data || []).map((d: any) => ({
    id: d.id,
    userId: d.user_id,
    videoId: d.video_id ?? undefined,
    videoTitle: d.analyzed_videos?.title ?? undefined,
    creatorName: d.analyzed_videos?.creator_name ?? undefined,
    extractedData: d.extracted_data,
    isUserSaved: d.is_user_saved,
    savedAt: d.saved_at ?? undefined,
    expiresAt: d.expires_at,
    createdAt: d.created_at,
  }));
}

export async function markDraftAsSaved(id: string): Promise<void> {
  const userId = await requireUserId();
  const { error } = await supabase
    .from('video_protocol_drafts')
    .update({
      is_user_saved: true,
      saved_at: new Date().toISOString(),
      expires_at: new Date('2099-12-31').toISOString(),
    })
    .eq('id', id)
    .eq('user_id', userId);
  if (error) throwWithContext("Couldn't save draft", error);
  notify('video_protocol_drafts');
}

export async function deleteVideoProtocolDraft(id: string): Promise<void> {
  const userId = await requireUserId();
  const { error } = await supabase
    .from('video_protocol_drafts')
    .delete()
    .eq('id', id)
    .eq('user_id', userId);
  if (error) throwWithContext("Couldn't delete draft", error);
  notify('video_protocol_drafts');
}
